import { Fragment, memo, useCallback, useEffect, useState } from "react";
import { IActionGroupSkill, ICategories, ISkill } from "types";
import { ENDPOINTS } from "const";
import {
  Card,
  StarRatingItem,
  Loader,
  BorderBadge,
} from "components";
import {
  useCreateSolutionRating,
  useUpdateSolutionRating,
} from "modules/Solution/SolutionsCard/SolutionsCard";
import { useMe, useSolutionRatings } from "hooks";
import { showErrorMessage, showSuccessMessage } from "utils";
import { useQueryClient } from "react-query";
import { XIcon, LocationMarkerIcon } from "@heroicons/react/outline";
import classnames from "classnames";
import { ReactComponent as RatingStar } from "assets/images/rating-star.svg";
import Avatar from "react-avatar";

interface IProps {
  title: string;
  location?: string;

  profileImage: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  createdAt: string | undefined;
  id: string | undefined;
  rating: number;
  skills?: Partial<IActionGroupSkill<Partial<ISkill>>>[];
  email: string | undefined;
  forceHoverIssue?: boolean;
  issueType: "regular" | "city" | "country";
  setHoveredIssueType: React.Dispatch<
    React.SetStateAction<"regular" | "city" | "country" | undefined>
  >;
  setIsIssueHovered?: React.Dispatch<React.SetStateAction<boolean>>;
  setIssueHoveredProps?: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
    }>
  >;
  geoLocation: {
    lat?: number | undefined;
    lng?: number | undefined;
  };
  solutionId: string | undefined;
  primaryClassification: keyof ICategories<any>;
  secondaryClassification: keyof ICategories<any>;
  innerRef?: (node?: Element | null | undefined) => void;
  score: number | undefined;
  tags?: string[] | undefined;
  isSoftDeleted?: boolean;
}

function SolutionCard({
  title,
  location,

  profileImage,
  firstName = "",
  lastName = "",
  createdAt = "",
  rating,
  issueType,
  id,
  skills,
  solutionId,
  setHoveredIssueType,
  setIsIssueHovered,
  setIssueHoveredProps,
  forceHoverIssue,
  geoLocation,
  email,
  innerRef,
  score,
  tags,
  isSoftDeleted,
}: IProps) {
  const date =
    createdAt &&
    new Date(createdAt).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  const { data: meData } = useMe();
  const queryClient = useQueryClient();

  const [showRateSolutionPopup, setShowRateSolutionPopup] = useState(false);
  const { data: solutionRatingData } = useSolutionRatings({
    solutionId: solutionId,
    userId: meData?.data.id,
    enabled: !!meData?.data.id && showRateSolutionPopup,
  });

  const [ratingValue, setRatingValue] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (
      solutionRatingData &&
      solutionRatingData?.data.items.length > 0 &&
      solutionRatingData?.data.items[0].rate
    ) {
      setRatingValue(solutionRatingData?.data.items[0].rate);
    }
  }, [solutionRatingData]);

  const {
    mutateAsync: mutateCreateIssueRatingAsync,
    isLoading: loadingCreate,
  } = useCreateSolutionRating({
    solutionId: solutionId,
  });
  const {
    mutateAsync: mutateUpdateIssueRatingAsync,
    isLoading: loadingUpdate,
  } = useUpdateSolutionRating({
    solutionRatingId:
      solutionRatingData && solutionRatingData?.data.items.length > 0
        ? solutionRatingData?.data.items[0].id
        : undefined,
  });

  const createRate = useCallback(
    (ratingValue: number) => {
      mutateCreateIssueRatingAsync({
        solution: solutionId,
        rate: ratingValue,
      })
        .then(() => {
          queryClient.invalidateQueries([ENDPOINTS.SOLUTIONS]);
          queryClient.resetQueries([ENDPOINTS.SOLUTIONS]);
          queryClient.invalidateQueries([ENDPOINTS.SOLUTION_RATING]);
          queryClient.invalidateQueries([ENDPOINTS.ISSUES]);
          queryClient.invalidateQueries([ENDPOINTS.ISSUE]);
          showSuccessMessage("Solution rating has been created!");
          setShowRateSolutionPopup(false);
        })
        .catch(() => {
          showErrorMessage("Error while updating rating!");
          setShowRateSolutionPopup(false);
        });
    },
    [solutionId, queryClient, mutateCreateIssueRatingAsync]
  );

  const updateRate = useCallback(
    (ratingValue: number) => {
      mutateUpdateIssueRatingAsync({
        solution: solutionId,
        rate: ratingValue,
      })
        .then(() => {
          queryClient.invalidateQueries([ENDPOINTS.SOLUTIONS]);
          queryClient.resetQueries([ENDPOINTS.SOLUTIONS]);
          queryClient.invalidateQueries([ENDPOINTS.SOLUTION_RATING]);
          queryClient.invalidateQueries([ENDPOINTS.ISSUES]);
          showSuccessMessage("Solution rating has been updated!");
          setShowRateSolutionPopup(false);
        })
        .catch(() => {
          showErrorMessage("Error while updating rating!");
          setShowRateSolutionPopup(false);
        });
    },
    [solutionId, queryClient, mutateUpdateIssueRatingAsync]
  );

  const fullName = `${firstName} ${lastName}`.trim() || email || "Anonymous";
  const formatRating = (val: number) => {
    return val % 1 === 0 ? val : val.toFixed(1);
  };

  return (
    <Card
      className={classnames("maxmd:flex-1 !gap-3 !py-5 h-full solution-card-content", {
        "bg-gray-100 pointer-events-none select-none": isSoftDeleted,
      })}
      issueType={issueType}
      setHoveredIssueType={setHoveredIssueType}
      forceHoverIssue={forceHoverIssue}
      setIsIssueHovered={setIsIssueHovered}
      setIssueHoveredProps={setIssueHoveredProps}
      geoLocation={geoLocation}
      showHeaderDivider={false}
      showSubHeaderDivider={false}
      showContent={false}
      showFooterDivider={true}
      header={
        <Fragment>
          {/* Title - Prominent */}
          <h3
            className="font-semibold text-base leading-6 line-clamp-2 mb-2"
            ref={innerRef}
            style={{
              color: "#1C1917",
            }}
          >
            {title}
          </h3>

          {/* Location */}
          {location && (
            <div className="flex items-center gap-1 text-stone-400 mb-2">
              <LocationMarkerIcon className="h-3.5 w-3.5" />
              <span
                className="text-xs truncate"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                {location}
              </span>
            </div>
          )}

          {/* Skills Badges - Horizontal row */}
          <div className="flex flex-wrap items-center gap-2">
            {skills?.slice(0, 2).map((skill) => (
              <BorderBadge
                key={skill.id}
                classificationName={skill?.skill?.name}
                variant="outline"
              >
                {skill?.skill?.name}
              </BorderBadge>
            ))}
            {skills && skills.length > 2 && (
              <span
                className="text-xs text-stone-400"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                +{skills.length - 2} more
              </span>
            )}
            {(!skills || skills.length < 1) && (
              <span
                className="text-xs text-stone-400 italic"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                No skills assigned
              </span>
            )}
          </div>
        </Fragment>
      }
      classNames={{
        header: "!gap-0 !pt-0",
        subHeader: "hidden",
        content: "flex-grow",
        footer: "!pt-3 !pb-0",
      }}
      footer={
        <div className="flex items-center justify-between w-full">
          {/* Author info */}
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 shrink-0 rounded-full overflow-hidden border border-stone-200">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <Avatar size="28" round color="#D6D3D1" name={fullName} />
              )}
            </div>
            <div className="flex flex-col">
              <span
                className="text-sm text-stone-600 font-medium truncate max-w-[120px]"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                {fullName}
              </span>
              <span
                className="text-xs text-stone-400"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                {date}
              </span>
            </div>
          </div>

          {/* Rating */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (!!meData?.data.id) {
                setShowRateSolutionPopup((val) => !val);
              }
            }}
            className="relative"
          >
            <div
              className={classnames(
                "flex items-center gap-1 px-2.5 py-1 rounded-full cursor-pointer transition-all",
                {
                  "hover:ring-2 hover:ring-terracotta/30": !!meData?.data.id,
                  "ring-2 ring-terracotta/50": showRateSolutionPopup,
                }
              )}
              style={{ background: "#F5F5F4" }}
            >
              <span
                className="text-xs font-medium text-stone-600"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                {rating ? formatRating(rating) : "0"}
              </span>
              <RatingStar height={10} width={10} fill="#C2410C" />
            </div>

            {/* Rating popup */}
            {showRateSolutionPopup && (
              <div
                className="absolute right-0 bottom-full mb-2 z-20 rounded-xl shadow-lg p-3"
                style={{
                  background: "#FFFEF9",
                  border: "1px solid #E7E5E4",
                  minWidth: "200px",
                }}
              >
                {loadingCreate || loadingUpdate ? (
                  <div className="w-full h-[60px] flex items-center justify-center">
                    <Loader type="rings" globalLoader={false} size="tiny" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-xs font-medium text-stone-600"
                        style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                      >
                        Rate this solution
                      </span>
                      <XIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRateSolutionPopup(false);
                        }}
                        className="h-4 w-4 text-stone-400 cursor-pointer hover:text-stone-600"
                      />
                    </div>
                    <StarRatingItem
                      starSize="small"
                      classNames={{
                        label: "hidden",
                        input: "!gap-1",
                      }}
                      title=""
                      ratingValue={ratingValue || 0}
                      onClick={(ratingValue) => {
                        if (
                          ratingValue &&
                          solutionRatingData &&
                          solutionRatingData?.data.items.length === 0
                        ) {
                          createRate(ratingValue);
                        }
                        if (
                          ratingValue &&
                          solutionRatingData &&
                          solutionRatingData?.data.items.length > 0
                        ) {
                          updateRate(ratingValue);
                        }
                      }}
                      setRatingValue={setRatingValue}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      }
      tags={
        tags &&
        tags?.length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 px-6 pt-2">
            {tags?.map((tag) => (
              <BorderBadge key={tag} classificationName={tag} variant="tag">
                {tag}
              </BorderBadge>
            ))}
          </div>
        )
      }
    />
  );
}

export default memo(SolutionCard);
